// src/utils/excelParser.js
// Utilidad única para leer archivos Excel. Centraliza la lógica que antes
// estaba duplicada en ImportadorArticulos e ImportadorOrdenes, e incorpora
// validación de tamaño y extensión.
import * as XLSX from 'xlsx';

const DEFAULT_MAX_SIZE_MB = 5;
const DEFAULT_VALID_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

/**
 * Lee un archivo Excel y devuelve las filas del primer sheet como array de objetos.
 * @param {File} file - El archivo seleccionado por el usuario.
 * @param {{ maxSizeMb?: number, validExtensions?: string[] }} [options]
 * @returns {Promise<Array<object>>}
 */
export const parseExcelFile = (file, options = {}) => {
  const maxSizeMb = options.maxSizeMb || DEFAULT_MAX_SIZE_MB;
  const validExtensions = options.validExtensions || DEFAULT_VALID_EXTENSIONS;

  return new Promise((resolve, reject) => {
    if (!file) {
      return reject(new Error('No se seleccionó ningún archivo.'));
    }

    if (file.size > maxSizeMb * 1024 * 1024) {
      return reject(new Error(`El archivo supera el límite de ${maxSizeMb} MB.`));
    }

    const dotIndex = file.name.lastIndexOf('.');
    const extension = dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : '';
    if (!validExtensions.includes(extension)) {
      return reject(
        new Error(`Formato no válido. Formatos permitidos: ${validExtensions.join(', ')}`)
      );
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let datos = e.target.result;
        let opciones = { type: 'array' };

        if (extension === '.csv') {
          // Un CSV es texto plano y no declara su codificación. Antes se leía
          // como binario, que interpreta los bytes como latin1: un encabezado
          // "Código" llegaba como "CÃ³digo" y la columna no se detectaba.
          // Se intenta UTF-8 (lo que exportan Excel moderno y Google Sheets) y,
          // si los bytes no son UTF-8 válido, se cae a windows-1252 (el CSV
          // clásico de Excel en español).
          try {
            datos = new TextDecoder('utf-8', { fatal: true }).decode(datos);
          } catch {
            datos = new TextDecoder('windows-1252').decode(datos);
          }
          opciones = { type: 'string' };
        }

        const workbook = XLSX.read(datos, opciones);
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(firstSheet);
        resolve(json);
      } catch (err) {
        reject(new Error('Error al procesar el archivo. Verifica el formato.'));
      }
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsArrayBuffer(file);
  });
};

export default parseExcelFile;
